export type ApiErrorCode =
  | 'PROVIDER_UNAVAILABLE'
  | 'PROVIDER_TIMEOUT'
  | 'SCHEMA_VALIDATION_FAILED'
  | 'CONTENT_REJECTED'
  | 'RATE_LIMITED'
  | 'INVALID_INPUT'
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'INTERNAL_ERROR'

  | 'NETWORK_ERROR';

export interface ApiFieldError {
  field: string;
  reason: string;
}

export interface ApiErrorBody {
  code: ApiErrorCode;
  message: string;
  retryable: boolean;
  details?: ApiFieldError[];
  correlation_id?: string;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly retryable: boolean;
  readonly details: ApiFieldError[];
  readonly correlationId: string | undefined;

  constructor(status: number, body: ApiErrorBody, cause?: unknown) {
    super(body.message, cause === undefined ? undefined : { cause });
    this.name = 'ApiError';
    this.code = body.code;
    this.status = status;
    this.retryable = body.retryable;
    this.details = body.details ?? [];
    this.correlationId = body.correlation_id;
  }

  get isTransient(): boolean {
    return this.retryable;
  }
}

const API_PREFIX = '/api/v1';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

function isErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v['code'] === 'string' && typeof v['message'] === 'string';
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit & { signal?: AbortSignal } = {},
): Promise<T> {
  const url = `${BASE_URL}${API_PREFIX}${path}`;

  let response: Response;
  try {
    const isFormData =
      typeof FormData !== 'undefined' && init.body instanceof FormData;

    response = await fetch(url, {
      credentials: 'same-origin',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (cause) {
    throw new ApiError(
      0,
      {
        code: 'NETWORK_ERROR',
        message: 'Could not reach the server. Check your connection and try again.',
        retryable: true,
      },
      cause,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, {
        code: 'INTERNAL_ERROR',
        message: 'An internal error occurred.',
        retryable: false,
      });
    }
  }

  if (!response.ok) {
    const body = parsed as { error?: unknown } | undefined;
    if (body && isErrorBody(body.error)) {
      throw new ApiError(response.status, body.error);
    }
    throw new ApiError(response.status, {
      code: 'INTERNAL_ERROR',
      message: 'An internal error occurred.',
      retryable: response.status >= 500,
    });
  }

  return parsed as T;
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>(path, signal ? { signal } : {});
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: 'PUT',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function apiDelete<T = void>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE' });
}

export function apiUpload<T>(
  path: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value);
  }
  return apiRequest<T>(path, { method: 'POST', body: form });
}
